import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, XCircle, ArrowRight, ArrowCounterClockwise, Lightning } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function prettyPlan(planId) {
  const value = String(planId || "").replace(/_/g, " ").trim();
  if (!value) return "your purchase";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function readMetadata(tx) {
  const metadata = tx?.metadata || {};
  return metadata && typeof metadata === "object" ? metadata : {};
}

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh, refreshUsage } = useAuth();
  const [state, setState] = useState("polling");
  const [tx, setTx] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        setTx(data);
        if (data.payment_status === "paid") {
          setState("paid");
          await refresh();
          if (refreshUsage) await refreshUsage();
          return;
        }
        if (data.status === "expired") { setState("expired"); return; }
        if (attemptsRef.current >= 8) { setState("failed"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (attemptsRef.current >= 8) { setState("failed"); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh, refreshUsage]);

  const metadata = useMemo(() => readMetadata(tx), [tx]);
  const purchaseType = metadata.purchase_type || tx?.type || (String(tx?.plan_id || "").startsWith("addon_") ? "addon" : "subscription");
  const credits = Number(metadata.credits || tx?.credits || 0);
  const isAddon = purchaseType === "addon" || String(tx?.plan_id || "").startsWith("addon_");

  return (
    <div className="container-wide py-24">
      <div className="max-w-2xl mx-auto">
        {state === "polling" && (
          <div className="border border-[#E5E7EB] p-10 bg-white text-center" data-testid="checkout-polling">
            <ArrowCounterClockwise size={28} weight="bold" className="mx-auto animate-spin text-[#002FA7]" />
            <p className="mt-4 font-display text-2xl font-extrabold">Confirming your payment…</p>
            <p className="mt-2 text-[#525252]">This usually takes a couple of seconds.</p>
          </div>
        )}

        {state === "paid" && (
          <div className="border border-[#16A34A] p-10 bg-white" data-testid="checkout-success">
            <CheckCircle size={40} weight="duotone" className="text-[#16A34A]" />
            <p className="mt-4 font-mono text-xs uppercase tracking-[0.22em] text-[#16A34A]">Payment successful</p>
            <h1 className="mt-2 font-display text-4xl font-black tracking-tighter">
              {isAddon ? "Credits added." : "You're in."}
            </h1>
            <p className="mt-4 text-[#525252] text-lg">
              {isAddon
                ? `${credits || "Your extra"} generation credits have been added to your account. You can keep generating from the studio straight away.`
                : `Thanks for upgrading${tx?.plan_id ? ` to ${prettyPlan(tx.plan_id)}` : ""}. Your plan access will update automatically.`}
            </p>

            {isAddon && (
              <div className="mt-6 border border-[#BFDBFE] bg-[#EFF6FF] p-4 flex gap-3 text-sm text-[#1E40AF]">
                <Lightning size={20} weight="duotone" className="shrink-0 mt-0.5" />
                <p>
                  Ordeneli uses your monthly allowance first. When that is finished, new generations use your add-on credits.
                </p>
              </div>
            )}

            <div className="mt-8 flex gap-3 flex-wrap">
              <Link to="/dashboard" className="btn-primary" data-testid="success-to-dashboard">
                Open the studio <ArrowRight size={16} weight="bold" />
              </Link>
              <Link to="/pricing" className="btn-secondary" data-testid="success-to-pricing">
                View plan and credits
              </Link>
              <Link to="/history" className="btn-secondary" data-testid="success-to-history">
                View your history
              </Link>
            </div>
            {tx && (
              <div className="mt-8 border-t border-[#E5E7EB] pt-4 text-xs font-mono text-[#737373] uppercase tracking-[0.18em]">
                Session {sessionId?.slice(0, 18)}… · £{((tx.amount_total || 0) / 100).toFixed(2)} {tx.currency?.toUpperCase()} · {prettyPlan(tx.plan_id)}
              </div>
            )}
          </div>
        )}

        {(state === "failed" || state === "expired") && (
          <div className="border border-[#DC2626] p-10 bg-white" data-testid="checkout-failed">
            <XCircle size={40} weight="duotone" className="text-[#DC2626]" />
            <p className="mt-4 font-mono text-xs uppercase tracking-[0.22em] text-[#DC2626]">
              {state === "expired" ? "Session expired" : "Payment not confirmed"}
            </p>
            <h1 className="mt-2 font-display text-4xl font-black tracking-tighter">
              {state === "expired" ? "Let's try that again." : "We couldn't confirm your payment yet."}
            </h1>
            <p className="mt-4 text-[#525252] text-lg">
              {state === "expired"
                ? "Your Stripe session expired. You can start over on the pricing page."
                : "If you completed payment, Stripe may still be sending the confirmation. Refresh usage from the dashboard in a moment."}
            </p>
            <div className="mt-8 flex gap-3 flex-wrap">
              <Link to="/pricing" className="btn-primary">
                Back to pricing <ArrowRight size={16} weight="bold" />
              </Link>
              <Link to="/dashboard" className="btn-secondary">Go to studio</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
